import React, { useState } from 'react';
import { formatCurrency, validatePaymentAmount, PAYMENT_TYPES } from '../utils/checkoutCalculations';
import { voucherActions } from '../../../_actions/voucher.actions';
import useSites from '../../multiLocation/useSites';

/**
 * VoucherScreen
 * Two-step flow: 1) Enter voucher code  2) Confirm redemption amount
 * Matches the checkout design language (co- classes).
 */
const VoucherScreen = ({ state, actions, dispatch, onBack, currencySymbol }) => {
  const fmt = (amount) => formatCurrency(amount, currencySymbol);
  const { sites } = useSites();

  // Step state
  const [step, setStep] = useState('enter'); // 'enter' | 'confirm'
  const [code, setCode] = useState('');
  const [voucher, setVoucher] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1: Look up voucher by code
  const handleLookup = async () => {
    if (!code.trim()) {
      setError('Please enter a voucher code');
      return;
    }
    setError('');
    setLoading(true);

    try {
      const response = await dispatch(voucherActions.getVoucherByCode(code.trim()));
      const responseData = response?.data?.responseData || response?.data;

      if (!responseData || responseData.statusCode === 0) {
        setError('Voucher not found. Please check the code.');
        setLoading(false);
        return;
      }

      let found = null;

      // Format 1: New API (responseData.data.id directly)
      if (responseData.data?.id) {
        const v = responseData.data;
        found = {
          _id: v.id,
          code: v.code,
          name: v.catalogName || v.name || '',
          faceValue: v.faceValue,
          value: v.faceValue,
          remainingValue: v.remainingValue,
          expiresAt: v.expiresAt,
          issuedAt: v.issuedAt || v.createdAt,
          purchaser: v.purchaser?.name || v.purchaserDetails?.name || '',
          type: v.type,
          currency: v.currency,
          // The API puts the sister-site marker beside `data`, not inside it.
          soldAtSalonId: v.soldAtSalonId || responseData.soldAtSalonId,
        };
      }
      // Format 2: Old API (voucher + voucherSale)
      else if (responseData.data?.voucher && responseData.data?.voucherSale) {
        const vData = responseData.data.voucher.data || responseData.data.voucher;
        const vSale = responseData.data.voucherSale;
        found = {
          _id: vData.id || vData._id,
          code: vData.code,
          name: vData.catalogName || vData.name || '',
          faceValue: vData.value,
          value: vData.value,
          remainingValue: vSale.remainingValue,
          expiresAt: vSale.expiresAt,
          issuedAt: vSale.issuedAt || vSale.createdAt,
          purchaser: vSale.purchaser?.name || vSale.purchaserDetails?.name || '',
          type: vData.type,
          currency: vData.currency,
          soldAtSalonId: vData.soldAtSalonId || responseData.data.soldAtSalonId,
        };
      }
      // Format 3: Direct responseData
      else if (responseData._id || responseData.id) {
        found = {
          _id: responseData._id || responseData.id,
          code: responseData.code,
          name: responseData.catalogName || responseData.name || '',
          faceValue: responseData.value || responseData.faceValue,
          value: responseData.value || responseData.faceValue,
          remainingValue: responseData.remainingValue || responseData.value,
          expiresAt: responseData.expiresAt,
          issuedAt: responseData.issuedAt || responseData.createdAt,
          purchaser: responseData.purchaser?.name || responseData.purchaserDetails?.name || '',
          type: responseData.type,
          currency: responseData.currency,
          soldAtSalonId: responseData.soldAtSalonId,
        };
      }

      if (!found) {
        setError('Voucher not found. Please check the code.');
        setLoading(false);
        return;
      }

      const remaining = parseFloat(found.remainingValue || 0);
      if (remaining <= 0) {
        setError('This voucher has no remaining balance');
        setLoading(false);
        return;
      }

      found.remainingValue = remaining;
      setVoucher(found);
      // Default amount: lesser of voucher balance or checkout balance
      const defaultAmount = Math.min(remaining, state.balanceRemaining);
      setAmount(defaultAmount.toFixed(2));
      setStep('confirm');
    } catch (err) {
      setError('Voucher not found. Please check the code.');
    }
    setLoading(false);
  };

  // Step 2: Redeem voucher
  const handleRedeem = async () => {
    const redeemAmount = parseFloat(amount);
    if (isNaN(redeemAmount) || redeemAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    if (redeemAmount > voucher.remainingValue) {
      setError(`Amount exceeds voucher balance (${fmt(voucher.remainingValue)})`);
      return;
    }
    const validation = validatePaymentAmount(redeemAmount, state.balanceRemaining);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const response = await dispatch(voucherActions.redeemVoucher({
        voucherCode: voucher.code || code.trim(),
        amount: redeemAmount,
      }));

      // Extract redemption index for later booking association
      const redemptionData = response?.data?.data || response?.data?.responseData?.data || {};
      const redemptions = redemptionData?.voucher?.data?.redemptions || redemptionData?.voucher?.redemptions || redemptionData?.redemptions || [];
      const redemptionIndex = redemptions.length > 0 ? redemptions.length - 1 : 0;

      // Add as transaction
      actions.addTransaction({
        paymentType: PAYMENT_TYPES.VOUCHER,
        amount: redeemAmount,
        label: `Voucher (${(voucher.code || code.trim()).slice(-6)})`,
        voucherId: voucher._id,
        voucherCode: voucher.code || code.trim(),
        redemptionIndex,
      });

      // Return to summary (addTransaction auto-advances if fully paid)
      if (state.balanceRemaining - redeemAmount > 0.01) {
        actions.setScreen('summary');
      }
    } catch (err) {
      setError('Failed to redeem voucher. Please try again.');
    }
    setLoading(false);
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Enter') handleLookup();
  };

  const handleAmountKeyDown = (e) => {
    if (e.key === 'Enter') handleRedeem();
  };

  const soldAtSite = voucher && voucher.soldAtSalonId
    ? sites.find((site) => String(site.salonId) === String(voucher.soldAtSalonId))
    : null;

  return (
    <div className="co-voucher">
      {/* Header */}
      <div className="co-screen-header">
        <button className="co-back-btn" onClick={step === 'confirm' ? () => { setStep('enter'); setError(''); } : onBack}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <h2 className="co-screen-title">Redeem Voucher</h2>
      </div>

      {/* Step 1: Enter Code */}
      {step === 'enter' && (
        <div className="co-section">
          <div className="co-section-title">Voucher Code</div>
          <div className="co-voucher-input-wrap">
            <input
              className="co-voucher-code-input"
              type="text"
              placeholder="Enter voucher code"
              value={code}
              onChange={e => { setCode(e.target.value.toUpperCase()); setError(''); }}
              onKeyDown={handleCodeKeyDown}
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
            />
          </div>

          {error && <p className="co-error-msg">{error}</p>}

          <button
            className="co-btn co-btn--primary"
            style={{ width: '100%', marginTop: 20 }}
            onClick={handleLookup}
            disabled={loading || !code.trim()}
          >
            {loading ? 'Looking up...' : 'Find Voucher'}
          </button>
        </div>
      )}

      {/* Step 2: Confirm Redemption */}
      {step === 'confirm' && voucher && (
        <div className="co-section">
          {/* Voucher card */}
          <div className="co-voucher-card">
            <div className="co-voucher-card-header">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
                <path d="M18 12a2 2 0 0 0 0 4h4v-4h-4z" />
              </svg>
              <div>
                <span className="co-voucher-card-code">{voucher.code || code}</span>
                {voucher.name && <span className="co-voucher-card-name">{voucher.name}</span>}
              </div>
            </div>

            <div className="co-voucher-card-details">
              <div className="co-voucher-card-row">
                <span>Face Value</span>
                <span>{fmt(voucher.faceValue || voucher.value)}</span>
              </div>
              <div className="co-voucher-card-row">
                <span>Remaining Balance</span>
                <span style={{ fontWeight: 600, color: '#34C759' }}>{fmt(voucher.remainingValue)}</span>
              </div>
              {voucher.purchaser && (
                <div className="co-voucher-card-row">
                  <span>Purchased by</span>
                  <span>{voucher.purchaser}</span>
                </div>
              )}
              {soldAtSite && (
                <div className="co-voucher-card-row">
                  <span>Sold at</span>
                  <span style={{ fontWeight: 600 }}>{soldAtSite.salonName}</span>
                </div>
              )}
              {voucher.issuedAt && (
                <div className="co-voucher-card-row">
                  <span>Sold</span>
                  <span>{new Date(voucher.issuedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
              {voucher.expiresAt && (
                <div className="co-voucher-card-row">
                  <span>Expires</span>
                  <span>{new Date(voucher.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              )}
            </div>
          </div>

          {/* Amount to redeem */}
          <div className="co-section-title" style={{ marginTop: 24 }}>Amount to Redeem</div>
          <div className="co-voucher-amount-wrap">
            <span className="co-currency-symbol">{currencySymbol}</span>
            <input
              className="co-amount-input"
              type="number"
              step="0.01"
              min="0.01"
              max={Math.min(voucher.remainingValue, state.balanceRemaining)}
              value={amount}
              onChange={e => { setAmount(e.target.value); setError(''); }}
              onKeyDown={handleAmountKeyDown}
              autoFocus
            />
          </div>

          <p className="co-voucher-hint">
            Balance due: {fmt(state.balanceRemaining)} / Voucher balance: {fmt(voucher.remainingValue)}
          </p>

          {error && <p className="co-error-msg">{error}</p>}

          <button
            className="co-btn co-btn--primary"
            style={{ width: '100%', marginTop: 20 }}
            onClick={handleRedeem}
            disabled={loading || !amount}
          >
            {loading ? 'Processing...' : `Redeem ${amount ? fmt(parseFloat(amount)) : ''}`}
          </button>
        </div>
      )}
    </div>
  );
};

export default VoucherScreen;
